// src/pages/tenant/TenantCompaniesPage.tsx
import React from 'react';
import {
  Box, Typography, Paper, Table, TableHead, TableRow, TableCell,
  TableBody, Button, Stack, TextField, InputAdornment, Pagination,
  Alert, Skeleton, Tooltip, IconButton, Divider, useMediaQuery,
  Dialog, DialogTitle, DialogContent, DialogActions, Grid,
  LinearProgress, Stepper, Step, StepLabel, Fade, Drawer,
  Chip, Menu, MenuItem, ListItemIcon, ListItemText, Avatar,
  CircularProgress,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import api from '../../services/api';

import BusinessIcon from '@mui/icons-material/Business';
import SearchIcon from '@mui/icons-material/Search';
import RefreshIcon from '@mui/icons-material/Refresh';
import AddIcon from '@mui/icons-material/Add';
import PeopleIcon from '@mui/icons-material/People';
import DevicesIcon from '@mui/icons-material/Devices';
import DirectionsCarIcon from '@mui/icons-material/DirectionsCar';
import PersonIcon from '@mui/icons-material/Person';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import EmailIcon from '@mui/icons-material/Email';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import VisibilityIcon from '@mui/icons-material/Visibility';
import BlockIcon from '@mui/icons-material/Block';
import PlayCircleIcon from '@mui/icons-material/PlayCircle';
import CloseIcon from '@mui/icons-material/Close';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import DeleteIcon from '@mui/icons-material/Delete';

// ─── Types ────────────────────────────────────────────────────────────────────
type CompanyRow = {
  _id: string;
  name?: string;
  email?: string;
  isActive?: boolean;
  adminAccepted?: boolean; // true = invite was accepted, false = still pending
  createdAt?: string;
  phone?: string;
  address?: string;
  stats?: { users?: number; devices?: number; vehicles?: number };
};

type OverviewUser = {
  _id: string;
  firstName: string;
  lastName: string;
  email: string;
  role: string;
  isActive: boolean;
  lastLogin?: string;
};

type OverviewData = {
  company: CompanyRow;
  stats: { users: number; devices: number; vehicles: number };
  users: OverviewUser[];
};

type ApiResponse = {
  success: boolean;
  data: CompanyRow[];
  pagination?: { page: number; limit: number; total: number; pages: number };
};

type CreateCompanyForm = {
  companyName: string;
  companyEmail: string;
  companyPhone: string;
  companyAddress: string;
  adminFirstName: string;
  adminLastName: string;
  adminEmail: string;
};

const EMPTY_FORM: CreateCompanyForm = {
  companyName: '', companyEmail: '', companyPhone: '',
  companyAddress: '', adminFirstName: '', adminLastName: '', adminEmail: '',
};

const STATUS_FILTERS = ['all', 'active', 'inactive'] as const;
type StatusFilter = typeof STATUS_FILTERS[number];
const STEPS = ['Company Info', 'Administrator'];

const getBaseUrl = (): string => {
  const { hostname, port, protocol } = window.location;
  const isLocalhost = hostname === 'localhost' || hostname === '127.0.0.1' || hostname === '::1';
  if (isLocalhost) return `${protocol}//${hostname}:${port || '6160'}`;
  return window.location.origin;
};

// ─── Status badge helper ──────────────────────────────────────────────────────
// Three distinct states:
//   Active          → company is active
//   Pending Invite  → company created but admin hasn't accepted invite yet
//   Suspended       → admin accepted invite but company was later suspended
const getStatusBadge = (c: CompanyRow) => {
  if (c.isActive) {
    return { label: 'Active', color: '#34d399', bg: 'rgba(52,211,153,0.12)' };
  }
  if (!c.adminAccepted) {
    return { label: 'Pending Invite', color: '#fbbf24', bg: 'rgba(251,191,36,0.10)' };
  }
  return { label: 'Suspended', color: '#f87171', bg: 'rgba(248,113,113,0.10)' };
};

const ROLE_COLORS: Record<string, string> = {
  company_admin: '#a78bfa', fleet_manager: '#38bdf8',
  alert_manager: '#fbbf24', maintenance: '#34d399', viewer: '#94a3b8',
};
const ROLE_LABELS: Record<string, string> = {
  company_admin: 'Company Admin', fleet_manager: 'Fleet Manager',
  alert_manager: 'Alert Manager', maintenance: 'Maintenance', viewer: 'Viewer',
};

// ─── Create Company Dialog ────────────────────────────────────────────────────
interface CreateCompanyDialogProps {
  open: boolean;
  onClose: () => void;
  onCreated: (newCompany: CompanyRow) => void;
}

const CreateCompanyDialog: React.FC<CreateCompanyDialogProps> = ({ open, onClose, onCreated }) => {
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const [step, setStep] = React.useState(0);
  const [form, setForm] = React.useState<CreateCompanyForm>(EMPTY_FORM);
  const [loading, setLoading] = React.useState(false);
  const [error, setError] = React.useState('');
  const [done, setDone] = React.useState(false);
  const [inviteExpiry, setInviteExpiry] = React.useState('');

  const reset = () => { setStep(0); setForm(EMPTY_FORM); setLoading(false); setError(''); setDone(false); setInviteExpiry(''); };
  const handleClose = () => { reset(); onClose(); };
  const set = (field: keyof CreateCompanyForm) => (e: React.ChangeEvent<HTMLInputElement>) =>
    setForm(prev => ({ ...prev, [field]: e.target.value }));
  const emailRe = /^\w+([.-]?\w+)*@\w+([.-]?\w+)*(\.\w{2,3})+$/;

  const validate = (): boolean => {
    setError('');
    if (step === 0) {
      if (!form.companyName.trim() || !form.companyEmail.trim()) { setError('Company name and email are required.'); return false; }
      if (!emailRe.test(form.companyEmail)) { setError('Please enter a valid company email.'); return false; }
    }
    if (step === 1) {
      if (!form.adminFirstName.trim() || !form.adminLastName.trim() || !form.adminEmail.trim()) { setError('All administrator fields are required.'); return false; }
      if (!emailRe.test(form.adminEmail)) { setError('Please enter a valid admin email.'); return false; }
    }
    return true;
  };

  const handleNext = async () => {
    if (!validate()) return;
    if (step === 1) {
      setLoading(true);
      try {
        const res = await api.post('/tenant-admin/companies', form);
        const created: CompanyRow = {
          _id: res.data.data.company._id,
          name: res.data.data.company.name,
          email: res.data.data.company.email,
          isActive: false,
          adminAccepted: false, // just created — invite not accepted yet
          stats: { users: 1, devices: 0, vehicles: 0 },
        };
        if (res.data.data.admin?.inviteExpiry) {
          setInviteExpiry(new Date(res.data.data.admin.inviteExpiry).toLocaleDateString('en-US', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' }));
        }
        setDone(true);
        onCreated(created);
      } catch (e: any) {
        setError(e?.response?.data?.message || e?.message || 'Failed to create company.');
      } finally {
        setLoading(false);
      }
    } else {
      setStep(s => s + 1);
    }
  };

  const border = isDark ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.08)';
  const subtext = isDark ? 'rgba(255,255,255,0.45)' : 'rgba(0,0,0,0.45)';
  const fieldSx = {
    '& .MuiOutlinedInput-root': {
      borderRadius: 2, fontSize: '0.875rem',
      background: isDark ? 'rgba(255,255,255,0.04)' : 'rgba(0,0,0,0.02)',
      '& fieldset': { borderColor: border },
      '&:hover fieldset': { borderColor: '#38bdf8' },
      '&.Mui-focused fieldset': { borderColor: '#38bdf8' },
    },
    '& .MuiInputLabel-root.Mui-focused': { color: '#38bdf8' },
  };
  const infoBannerSx = {
    p: 1.5, borderRadius: 2,
    background: isDark ? 'rgba(56,189,248,0.08)' : 'rgba(56,189,248,0.06)',
    border: '1px solid rgba(56,189,248,0.18)',
    display: 'flex', gap: 1.5, alignItems: 'flex-start',
  };

  return (
    <Dialog open={open} onClose={done ? handleClose : undefined} maxWidth="sm" fullWidth fullScreen={isMobile}
      PaperProps={{ sx: { borderRadius: isMobile ? 0 : 3, background: isDark ? '#1e1e1e' : '#fff', border: `1px solid ${border}` } }}>
      <DialogTitle sx={{ pb: 1 }}>
        <Stack direction="row" spacing={1.5} alignItems="center">
          <Box sx={{ width: 32, height: 32, borderRadius: 1.5, background: 'rgba(167,139,250,0.12)', display: 'grid', placeItems: 'center', flexShrink: 0 }}>
            <BusinessIcon sx={{ color: '#a78bfa', fontSize: 18 }} />
          </Box>
          <Typography sx={{ fontWeight: 900, fontSize: '1rem', color: isDark ? '#fff' : '#0f172a' }}>
            {done ? 'Company Created!' : 'Create New Company'}
          </Typography>
        </Stack>
      </DialogTitle>
      {loading && <LinearProgress sx={{ mx: 3 }} />}
      <DialogContent sx={{ pt: 2 }}>
        {!done && (
          <Stepper activeStep={step} alternativeLabel sx={{ mb: 3 }}>
            {STEPS.map(label => (
              <Step key={label}>
                <StepLabel sx={{ '& .MuiStepLabel-label': { fontSize: '0.75rem', fontWeight: 700, color: subtext }, '& .MuiStepLabel-label.Mui-active': { color: '#38bdf8' }, '& .MuiStepLabel-label.Mui-completed': { color: '#34d399' }, '& .MuiStepIcon-root.Mui-active': { color: '#38bdf8' }, '& .MuiStepIcon-root.Mui-completed': { color: '#34d399' } }}>
                  {label}
                </StepLabel>
              </Step>
            ))}
          </Stepper>
        )}
        {error && <Alert severity="error" onClose={() => setError('')} sx={{ mb: 2, borderRadius: 2, background: 'rgba(248,113,113,0.08)', border: '1px solid rgba(248,113,113,0.2)', color: '#f87171', '& .MuiAlert-icon': { color: '#f87171' } }}>{error}</Alert>}

        {/* Step 0 — Company Info */}
        {!done && step === 0 && (
          <Fade in><Grid container spacing={2}>
            <Grid item xs={12}><Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1 }}><BusinessIcon sx={{ color: '#a78bfa', fontSize: 18 }} /><Typography sx={{ fontWeight: 800, fontSize: '0.8rem', letterSpacing: '0.06em', textTransform: 'uppercase', color: subtext }}>Company Details</Typography></Stack></Grid>
            <Grid item xs={12}><TextField fullWidth label="Company Name *" value={form.companyName} onChange={set('companyName')} size="small" sx={fieldSx} /></Grid>
            <Grid item xs={12} sm={6}><TextField fullWidth label="Company Email *" type="email" value={form.companyEmail} onChange={set('companyEmail')} size="small" sx={fieldSx} /></Grid>
            <Grid item xs={12} sm={6}><TextField fullWidth label="Company Phone" value={form.companyPhone} onChange={set('companyPhone')} size="small" sx={fieldSx} /></Grid>
            <Grid item xs={12}><TextField fullWidth label="Address" value={form.companyAddress} onChange={set('companyAddress')} size="small" multiline rows={2} sx={fieldSx} /></Grid>
          </Grid></Fade>
        )}

        {/* Step 1 — Admin Info */}
        {!done && step === 1 && (
          <Fade in><Grid container spacing={2}>
            <Grid item xs={12}><Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1 }}><PersonIcon sx={{ color: '#38bdf8', fontSize: 18 }} /><Typography sx={{ fontWeight: 800, fontSize: '0.8rem', letterSpacing: '0.06em', textTransform: 'uppercase', color: subtext }}>Company Administrator</Typography></Stack></Grid>
            <Grid item xs={12} sm={6}><TextField fullWidth label="First Name *" value={form.adminFirstName} onChange={set('adminFirstName')} size="small" sx={fieldSx} /></Grid>
            <Grid item xs={12} sm={6}><TextField fullWidth label="Last Name *" value={form.adminLastName} onChange={set('adminLastName')} size="small" sx={fieldSx} /></Grid>
            <Grid item xs={12}><TextField fullWidth label="Admin Email *" type="email" value={form.adminEmail} onChange={set('adminEmail')} size="small" sx={fieldSx} helperText="An invitation email will be sent here — no password needed from you" /></Grid>
            <Grid item xs={12}>
              <Box sx={infoBannerSx}>
                <EmailIcon sx={{ color: '#38bdf8', fontSize: 18, mt: 0.2, flexShrink: 0 }} />
                <Box>
                  <Typography sx={{ fontSize: '0.78rem', fontWeight: 700, color: '#38bdf8', mb: 0.3 }}>Invite-only setup</Typography>
                  <Typography sx={{ fontSize: '0.73rem', color: subtext, lineHeight: 1.6 }}>
                    The admin will receive a secure link at <strong style={{ color: isDark ? '#fff' : '#0f172a' }}>{form.adminEmail || '…'}</strong> to set their own password. Link expires in <strong style={{ color: isDark ? '#fff' : '#0f172a' }}>7 days</strong>.
                  </Typography>
                  <Typography sx={{ fontSize: '0.69rem', color: subtext, mt: 0.75, fontFamily: 'monospace', wordBreak: 'break-all' }}>{getBaseUrl()}/accept-invitation/{'<token>'}</Typography>
                </Box>
              </Box>
            </Grid>
          </Grid></Fade>
        )}

        {/* Done */}
        {done && (
          <Fade in><Box textAlign="center" py={2}>
            <Box sx={{ width: 64, height: 64, borderRadius: '50%', background: 'rgba(52,211,153,0.12)', display: 'grid', placeItems: 'center', mx: 'auto', mb: 2 }}>
              <CheckCircleIcon sx={{ color: '#34d399', fontSize: 36 }} />
            </Box>
            <Typography sx={{ fontWeight: 900, fontSize: '1.1rem', color: isDark ? '#fff' : '#0f172a', mb: 2 }}>{form.companyName} is live!</Typography>
            <Box sx={{ ...infoBannerSx, textAlign: 'left' }}>
              <EmailIcon sx={{ color: '#38bdf8', fontSize: 18, mt: 0.2, flexShrink: 0 }} />
              <Box>
                <Typography sx={{ fontSize: '0.78rem', fontWeight: 800, color: '#38bdf8', mb: 0.5 }}>Invitation email sent ✓</Typography>
                <Typography sx={{ fontSize: '0.8rem', color: subtext, lineHeight: 1.6 }}>Invite sent to <strong style={{ color: isDark ? '#fff' : '#0f172a' }}>{form.adminEmail}</strong>. They'll click the link to set their password and activate their account.</Typography>
                {inviteExpiry && <Typography sx={{ fontSize: '0.72rem', color: subtext, mt: 0.75 }}>⏰ Expires: <strong style={{ color: isDark ? '#fff' : '#0f172a' }}>{inviteExpiry}</strong></Typography>}
              </Box>
            </Box>
          </Box></Fade>
        )}
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 3, pt: 1, gap: 1 }}>
        {done ? (
          <Button fullWidth variant="contained" onClick={handleClose} sx={{ borderRadius: 2, textTransform: 'none', fontWeight: 800, background: '#34d399', color: '#000', '&:hover': { background: '#2ecc71' } }}>Done</Button>
        ) : (
          <>
            <Button onClick={step === 0 ? handleClose : () => { setError(''); setStep(s => s - 1); }} disabled={loading} startIcon={step > 0 ? <ArrowBackIcon /> : undefined} sx={{ borderRadius: 2, textTransform: 'none', fontWeight: 700, color: subtext }}>{step === 0 ? 'Cancel' : 'Back'}</Button>
            <Button variant="contained" onClick={handleNext} disabled={loading} endIcon={step === 1 ? <EmailIcon /> : <ArrowForwardIcon />} sx={{ borderRadius: 2, textTransform: 'none', fontWeight: 800, background: '#38bdf8', color: '#000', '&:hover': { background: '#0ea5e9' }, '&:disabled': { background: 'rgba(56,189,248,0.3)' } }}>
              {loading ? 'Sending invite…' : step === 1 ? 'Create & Send Invite' : 'Next'}
            </Button>
          </>
        )}
      </DialogActions>
    </Dialog>
  );
};

// ─── Confirm Dialog ───────────────────────────────────────────────────────────
interface ConfirmDialogProps {
  open: boolean;
  title: string;
  message: string;
  confirmLabel: string;
  confirmColor: string;
  loading: boolean;
  onConfirm: () => void;
  onCancel: () => void;
}

const ConfirmDialog: React.FC<ConfirmDialogProps> = ({ open, title, message, confirmLabel, confirmColor, loading, onConfirm, onCancel }) => {
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';
  const border = isDark ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.08)';
  const subtext = isDark ? 'rgba(255,255,255,0.45)' : 'rgba(0,0,0,0.45)';
  return (
    <Dialog open={open} maxWidth="xs" fullWidth PaperProps={{ sx: { borderRadius: 3, background: isDark ? '#1e1e1e' : '#fff', border: `1px solid ${border}` } }}>
      <DialogContent sx={{ pt: 3 }}>
        <Stack spacing={2} alignItems="center" textAlign="center">
          <Box sx={{ width: 52, height: 52, borderRadius: '50%', background: 'rgba(248,113,113,0.12)', display: 'grid', placeItems: 'center' }}>
            <WarningAmberIcon sx={{ color: '#f87171', fontSize: 28 }} />
          </Box>
          <Typography sx={{ fontWeight: 900, fontSize: '1rem', color: isDark ? '#fff' : '#0f172a' }}>{title}</Typography>
          <Typography sx={{ fontSize: '0.85rem', color: subtext, lineHeight: 1.6 }}>{message}</Typography>
        </Stack>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 3, gap: 1 }}>
        <Button onClick={onCancel} disabled={loading} sx={{ borderRadius: 2, textTransform: 'none', fontWeight: 700, color: subtext }}>Cancel</Button>
        <Button variant="contained" onClick={onConfirm} disabled={loading} sx={{ borderRadius: 2, textTransform: 'none', fontWeight: 800, background: confirmColor, color: '#fff', '&:hover': { filter: 'brightness(0.9)' } }}>
          {loading ? <CircularProgress size={18} sx={{ color: '#fff' }} /> : confirmLabel}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

// ─── Overview Drawer ──────────────────────────────────────────────────────────
interface OverviewDrawerProps {
  companyId: string | null;
  onClose: () => void;
  onStatusChange: (companyId: string, isActive: boolean) => void;
}

const OverviewDrawer: React.FC<OverviewDrawerProps> = ({ companyId, onClose, onStatusChange }) => {
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';
  const [data, setData] = React.useState<OverviewData | null>(null);
  const [loading, setLoading] = React.useState(false);
  const [error, setError] = React.useState('');
  const [confirmOpen, setConfirmOpen] = React.useState(false);
  const [actionLoading, setActionLoading] = React.useState(false);

  React.useEffect(() => {
    if (!companyId) { setData(null); return; }
    setLoading(true); setError('');
    api.get(`/tenant-admin/companies/${companyId}/overview`)
      .then(res => setData(res.data.data))
      .catch(e => setError(e?.response?.data?.message || 'Failed to load overview'))
      .finally(() => setLoading(false));
  }, [companyId]);

  const handleToggleStatus = async () => {
    if (!data) return;
    setActionLoading(true);
    try {
      const newStatus = !data.company.isActive;
      await api.patch(`/tenant-admin/companies/${data.company._id}/status`, { isActive: newStatus });
      onStatusChange(data.company._id, newStatus);
      setData(prev => prev ? { ...prev, company: { ...prev.company, isActive: newStatus }, users: prev.users.map(u => ({ ...u, isActive: newStatus })) } : prev);
      setConfirmOpen(false);
    } catch (e: any) {
      setError(e?.response?.data?.message || 'Failed to update status');
    } finally {
      setActionLoading(false);
    }
  };

  const border = isDark ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.08)';
  const subtext = isDark ? 'rgba(255,255,255,0.45)' : 'rgba(0,0,0,0.45)';
  const surface = isDark ? '#1a1a1a' : '#f8fafc';
  const headingColor = isDark ? '#fff' : '#0f172a';
  const isActive = !!data?.company.isActive;

  return (
    <>
      <ConfirmDialog
        open={confirmOpen}
        title={isActive ? 'Suspend Company?' : 'Reactivate Company?'}
        message={isActive
          ? `Suspending "${data?.company.name}" will immediately lock out all ${data?.stats.users ?? 0} users — admins, fleet managers, maintenance staff and viewers.`
          : `Reactivating "${data?.company.name}" will restore access for all users in this company.`
        }
        confirmLabel={isActive ? 'Suspend Company' : 'Reactivate Company'}
        confirmColor={isActive ? '#f87171' : '#34d399'}
        loading={actionLoading}
        onConfirm={handleToggleStatus}
        onCancel={() => setConfirmOpen(false)}
      />
      <Drawer anchor="right" open={!!companyId} onClose={onClose}
        PaperProps={{ sx: { width: { xs: '100vw', sm: 480 }, background: isDark ? '#141414' : '#fff', borderLeft: `1px solid ${border}` } }}>
        {/* Header */}
        <Box sx={{ px: 3, py: 2, borderBottom: `1px solid ${border}`, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Stack direction="row" spacing={1.5} alignItems="center">
            <Box sx={{ width: 32, height: 32, borderRadius: 1.5, background: 'rgba(167,139,250,0.12)', display: 'grid', placeItems: 'center' }}>
              <BusinessIcon sx={{ color: '#a78bfa', fontSize: 18 }} />
            </Box>
            <Typography sx={{ fontWeight: 900, fontSize: '0.95rem', color: headingColor }}>{data?.company.name || 'Company Overview'}</Typography>
          </Stack>
          <IconButton size="small" onClick={onClose} sx={{ color: subtext }}><CloseIcon fontSize="small" /></IconButton>
        </Box>

        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: 300 }}>
            <CircularProgress size={32} sx={{ color: '#a78bfa' }} />
          </Box>
        ) : error ? (
          <Box sx={{ p: 3 }}><Alert severity="error" sx={{ borderRadius: 2 }}>{error}</Alert></Box>
        ) : data ? (
          <Box sx={{ overflowY: 'auto', height: '100%', pb: 10 }}>
            {/* Status banner */}
            <Box sx={{ mx: 3, mt: 2.5, p: 1.5, borderRadius: 2, background: isActive ? 'rgba(52,211,153,0.08)' : 'rgba(248,113,113,0.08)', border: `1px solid ${isActive ? 'rgba(52,211,153,0.2)' : 'rgba(248,113,113,0.2)'}`, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <Stack direction="row" spacing={1} alignItems="center">
                <Box sx={{ width: 8, height: 8, borderRadius: '50%', background: isActive ? '#34d399' : '#f87171' }} />
                <Typography sx={{ fontSize: '0.8rem', fontWeight: 800, color: isActive ? '#34d399' : '#f87171', textTransform: 'uppercase', letterSpacing: '0.06em' }}>
                  {isActive ? 'Active' : 'Suspended'}
                </Typography>
              </Stack>
              <Button size="small" startIcon={isActive ? <BlockIcon fontSize="small" /> : <PlayCircleIcon fontSize="small" />} onClick={() => setConfirmOpen(true)}
                sx={{ borderRadius: 1.5, textTransform: 'none', fontWeight: 800, fontSize: '0.75rem', background: isActive ? 'rgba(248,113,113,0.12)' : 'rgba(52,211,153,0.12)', color: isActive ? '#f87171' : '#34d399', '&:hover': { background: isActive ? 'rgba(248,113,113,0.2)' : 'rgba(52,211,153,0.2)' } }}>
                {isActive ? 'Suspend' : 'Reactivate'}
              </Button>
            </Box>

            {/* Company info */}
            <Box sx={{ mx: 3, mt: 2.5 }}>
              <Typography sx={{ fontSize: '0.7rem', fontWeight: 800, letterSpacing: '0.08em', textTransform: 'uppercase', color: subtext, mb: 1.5 }}>Company Info</Typography>
              <Paper elevation={0} sx={{ borderRadius: 2, border: `1px solid ${border}`, background: surface, overflow: 'hidden' }}>
                {[
                  { label: 'Email', value: data.company.email },
                  { label: 'Phone', value: data.company.phone || '—' },
                  { label: 'Address', value: data.company.address || '—' },
                  { label: 'Created', value: data.company.createdAt ? new Date(data.company.createdAt).toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' }) : '—' },
                ].map((item, i, arr) => (
                  <Box key={item.label} sx={{ px: 2, py: 1.25, borderBottom: i < arr.length - 1 ? `1px solid ${border}` : 'none', display: 'flex', justifyContent: 'space-between', gap: 2 }}>
                    <Typography sx={{ fontSize: '0.78rem', fontWeight: 700, color: subtext, flexShrink: 0 }}>{item.label}</Typography>
                    <Typography sx={{ fontSize: '0.78rem', fontWeight: 600, color: headingColor, textAlign: 'right', wordBreak: 'break-word' }}>{item.value}</Typography>
                  </Box>
                ))}
              </Paper>
            </Box>

            {/* Stats */}
            <Box sx={{ mx: 3, mt: 2.5 }}>
              <Typography sx={{ fontSize: '0.7rem', fontWeight: 800, letterSpacing: '0.08em', textTransform: 'uppercase', color: subtext, mb: 1.5 }}>Statistics</Typography>
              <Stack direction="row" spacing={1.5}>
                {[
                  { label: 'Users', value: data.stats.users, Icon: PeopleIcon, color: '#a78bfa', bg: 'rgba(167,139,250,0.10)' },
                  { label: 'Devices', value: data.stats.devices, Icon: DevicesIcon, color: '#fbbf24', bg: 'rgba(251,191,36,0.10)' },
                  { label: 'Vehicles', value: data.stats.vehicles, Icon: DirectionsCarIcon, color: '#f87171', bg: 'rgba(248,113,113,0.10)' },
                ].map(s => (
                  <Paper key={s.label} elevation={0} sx={{ flex: 1, p: 1.5, borderRadius: 2, border: `1px solid ${border}`, background: surface, textAlign: 'center' }}>
                    <Box sx={{ width: 32, height: 32, borderRadius: 1.5, background: s.bg, display: 'grid', placeItems: 'center', mx: 'auto', mb: 0.75 }}>
                      <s.Icon sx={{ fontSize: 16, color: s.color }} />
                    </Box>
                    <Typography sx={{ fontWeight: 900, fontSize: '1.2rem', color: headingColor }}>{s.value}</Typography>
                    <Typography sx={{ fontSize: '0.7rem', color: subtext, fontWeight: 700 }}>{s.label}</Typography>
                  </Paper>
                ))}
              </Stack>
            </Box>

            {/* Users list */}
            <Box sx={{ mx: 3, mt: 2.5 }}>
              <Typography sx={{ fontSize: '0.7rem', fontWeight: 800, letterSpacing: '0.08em', textTransform: 'uppercase', color: subtext, mb: 1.5 }}>
                Team Members ({data.users.length})
              </Typography>
              <Stack spacing={1}>
                {data.users.length === 0 ? (
                  <Typography sx={{ fontSize: '0.8rem', color: subtext, textAlign: 'center', py: 3 }}>No users yet</Typography>
                ) : data.users.map(u => {
                  const roleColor = ROLE_COLORS[u.role] || '#94a3b8';
                  const initials = `${u.firstName?.[0] || ''}${u.lastName?.[0] || ''}`.toUpperCase();
                  return (
                    <Paper key={u._id} elevation={0} sx={{ p: 1.5, borderRadius: 2, border: `1px solid ${border}`, background: surface, display: 'flex', alignItems: 'center', gap: 1.5 }}>
                      <Avatar sx={{ width: 34, height: 34, fontSize: '0.78rem', fontWeight: 800, background: `${roleColor}22`, color: roleColor }}>{initials}</Avatar>
                      <Box sx={{ flex: 1, minWidth: 0 }}>
                        <Typography sx={{ fontWeight: 800, fontSize: '0.82rem', color: headingColor, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{u.firstName} {u.lastName}</Typography>
                        <Typography sx={{ fontSize: '0.72rem', color: subtext, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{u.email}</Typography>
                      </Box>
                      <Stack spacing={0.5} alignItems="flex-end">
                        <Chip label={ROLE_LABELS[u.role] || u.role} size="small" sx={{ height: 20, fontSize: '0.65rem', fontWeight: 800, background: `${roleColor}18`, color: roleColor, border: 'none' }} />
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                          <Box sx={{ width: 5, height: 5, borderRadius: '50%', background: u.isActive ? '#34d399' : '#f87171' }} />
                          <Typography sx={{ fontSize: '0.65rem', color: u.isActive ? '#34d399' : '#f87171', fontWeight: 700 }}>{u.isActive ? 'Active' : 'Inactive'}</Typography>
                        </Box>
                      </Stack>
                    </Paper>
                  );
                })}
              </Stack>
            </Box>
          </Box>
        ) : null}
      </Drawer>
    </>
  );
};

// ─── Actions Menu ─────────────────────────────────────────────────────────────
interface ActionsMenuProps {
  company: CompanyRow;
  onView: () => void;
  onToggleStatus: () => void;
  onDelete: () => void;
}

const ActionsMenu: React.FC<ActionsMenuProps> = ({ company, onView, onToggleStatus, onDelete }) => {
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';
  const [anchor, setAnchor] = React.useState<null | HTMLElement>(null);
  const subtext = isDark ? 'rgba(255,255,255,0.45)' : 'rgba(0,0,0,0.45)';

  // Only allow suspend/reactivate if invite has been accepted
  // Pending-invite companies shouldn't be "suspended" — just delete them

  const canToggle = !!company.adminAccepted;
  
  return (
    <>
      <IconButton size="small" onClick={e => setAnchor(e.currentTarget)} sx={{ color: subtext }}>
        <MoreVertIcon fontSize="small" />
      </IconButton>
      <Menu anchorEl={anchor} open={Boolean(anchor)} onClose={() => setAnchor(null)}
        PaperProps={{ sx: { borderRadius: 2, background: isDark ? '#1e1e1e' : '#fff', border: `1px solid ${isDark ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.08)'}`, minWidth: 180, boxShadow: '0 8px 24px rgba(0,0,0,0.15)' } }}
        transformOrigin={{ horizontal: 'right', vertical: 'top' }}
        anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}>
 
        {/* View Overview — always available */}
        <MenuItem onClick={() => { setAnchor(null); onView(); }}>
          <ListItemIcon><VisibilityIcon fontSize="small" sx={{ color: '#38bdf8' }} /></ListItemIcon>
          <ListItemText primary="View Overview" primaryTypographyProps={{ fontSize: '0.82rem', fontWeight: 700 }} />
        </MenuItem>
 
        {/* Suspend/Reactivate — only if invite was accepted (active account) */}
        {canToggle && (
          <>
            <Divider sx={{ my: 0.5 }} />
            <MenuItem onClick={() => { setAnchor(null); onToggleStatus(); }} sx={{ color: company.isActive ? '#f87171' : '#34d399' }}>
              <ListItemIcon>
                {company.isActive
                  ? <BlockIcon fontSize="small" sx={{ color: '#f87171' }} />
                  : <PlayCircleIcon fontSize="small" sx={{ color: '#34d399' }} />
                }
              </ListItemIcon>
              <ListItemText
                primary={company.isActive ? 'Suspend Company' : 'Reactivate Company'}
                primaryTypographyProps={{ fontSize: '0.82rem', fontWeight: 700, color: company.isActive ? '#f87171' : '#34d399' }}
              />
            </MenuItem>
          </>
        )}
 
        {/* Delete — ALWAYS available for all companies including pending-invite */}
        <Divider sx={{ my: 0.5 }} />
        <MenuItem onClick={() => { setAnchor(null); onDelete(); }}>
          <ListItemIcon><DeleteIcon fontSize="small" sx={{ color: '#f87171' }} /></ListItemIcon>
          <ListItemText primary="Delete Company" primaryTypographyProps={{ fontSize: '0.82rem', fontWeight: 700, color: '#f87171' }} />
        </MenuItem>
 
      </Menu>
    </>
  );
};

// ─── Main Page ────────────────────────────────────────────────────────────────
const TenantCompaniesPage: React.FC = () => {
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const [search, setSearch] = React.useState('');
  const [status, setStatus] = React.useState<StatusFilter>('all');
  const [page, setPage] = React.useState(1);
  const limit = 20;

  const [rows, setRows] = React.useState<CompanyRow[]>([]);
  const [pages, setPages] = React.useState(1);
  const [total, setTotal] = React.useState(0);
  const [loading, setLoading] = React.useState(false);
  const [error, setError] = React.useState('');
  const [dialogOpen, setDialogOpen] = React.useState(false);
  const [overviewId, setOverviewId] = React.useState<string | null>(null);
  const [confirmTarget, setConfirmTarget] = React.useState<CompanyRow | null>(null);
  const [confirmLoading, setConfirmLoading] = React.useState(false);
  const [deleteTarget, setDeleteTarget] = React.useState<CompanyRow | null>(null);
  const [deleteLoading, setDeleteLoading] = React.useState(false);

  const fetchCompanies = React.useCallback(async () => {
    try {
      setLoading(true); setError('');
      const res = await api.get<ApiResponse>('/tenant-admin/companies', { params: { search, status, page, limit } });
      const body = res.data;
      const list = Array.isArray(body?.data) ? body.data : [];
      setRows(list);
      setPages(body?.pagination?.pages || 1);
      setTotal(body?.pagination?.total || list.length);
    } catch (e: any) {
      setError(e?.response?.data?.message || e?.message || 'Failed to load companies');
      setRows([]);
    } finally {
      setLoading(false);
    }
  }, [search, status, page, limit]);

  React.useEffect(() => { fetchCompanies(); }, [fetchCompanies]);
  React.useEffect(() => { setPage(1); }, [search, status]);

  const handleCompanyCreated = (newCompany: CompanyRow) => {
    setRows(prev => [newCompany, ...prev]);
    setTotal(prev => prev + 1);
    fetchCompanies();
  };

  const applyStatusChange = (companyId: string, isActive: boolean) => {
    setRows(prev => prev.map(r => r._id === companyId ? { ...r, isActive } : r));
  };

  const handleQuickToggle = async () => {
    if (!confirmTarget) return;
    setConfirmLoading(true);
    try {
      const newStatus = !confirmTarget.isActive;
      await api.patch(`/tenant-admin/companies/${confirmTarget._id}/status`, { isActive: newStatus });
      applyStatusChange(confirmTarget._id, newStatus);
      setConfirmTarget(null);
    } catch (e: any) {
      setError(e?.response?.data?.message || 'Failed to update status');
    } finally {
      setConfirmLoading(false);
    }
  };

  const handleDelete = async () => {
    if (!deleteTarget) return;
    setDeleteLoading(true);
    try {
      await api.delete(`/tenant-admin/companies/${deleteTarget._id}`);
      setRows(prev => prev.filter(r => r._id !== deleteTarget._id));
      setTotal(prev => prev - 1);
      setDeleteTarget(null);
    } catch (e: any) {
      setError(e?.response?.data?.message || 'Failed to delete company');
    } finally {
      setDeleteLoading(false);
    }
  };

  const surface = isDark ? 'rgba(255,255,255,0.03)' : '#ffffff';
  const border = isDark ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.08)';
  const subtext = isDark ? 'rgba(255,255,255,0.45)' : 'rgba(0,0,0,0.45)';
  const headingColor = isDark ? '#ffffff' : '#0f172a';

  return (
    <Box>
      <CreateCompanyDialog open={dialogOpen} onClose={() => setDialogOpen(false)} onCreated={handleCompanyCreated} />

      <OverviewDrawer
        companyId={overviewId}
        onClose={() => setOverviewId(null)}
        onStatusChange={(id, isActive) => applyStatusChange(id, isActive)}
      />

      <ConfirmDialog
        open={!!confirmTarget}
        title={confirmTarget?.isActive ? 'Suspend Company?' : 'Reactivate Company?'}
        message={confirmTarget?.isActive
          ? `Suspending "${confirmTarget?.name}" will immediately lock out ALL users in this company — admins, fleet managers, maintenance staff and viewers.`
          : `Reactivating "${confirmTarget?.name}" will restore access for all users in this company.`
        }
        confirmLabel={confirmTarget?.isActive ? 'Suspend Company' : 'Reactivate Company'}
        confirmColor={confirmTarget?.isActive ? '#f87171' : '#34d399'}
        loading={confirmLoading}
        onConfirm={handleQuickToggle}
        onCancel={() => setConfirmTarget(null)}
      />

      <ConfirmDialog
        open={!!deleteTarget}
        title="Delete Company?"
        message={`This will permanently delete "${deleteTarget?.name}" and ALL associated users, devices, vehicles and invoices. This cannot be undone.`}
        confirmLabel="Delete Permanently"
        confirmColor="#f87171"
        loading={deleteLoading}
        onConfirm={handleDelete}
        onCancel={() => setDeleteTarget(null)}
      />

      {/* ── Header ── */}
      <Stack direction={isMobile ? 'column' : 'row'} alignItems={isMobile ? 'flex-start' : 'center'} justifyContent="space-between" spacing={1.5} sx={{ mb: 3 }}>
        <Stack direction="row" spacing={1.5} alignItems="center">
          <Box sx={{ width: 36, height: 36, borderRadius: 2, background: 'rgba(167,139,250,0.12)', display: 'grid', placeItems: 'center' }}>
            <BusinessIcon sx={{ color: '#a78bfa', fontSize: 20 }} />
          </Box>
          <Box>
            <Typography variant="h5" sx={{ fontWeight: 900, lineHeight: 1.2, color: headingColor }}>Companies</Typography>
            <Typography variant="body2" sx={{ color: subtext, fontWeight: 500 }}>Manage all companies on this platform</Typography>
          </Box>
        </Stack>
        <Stack direction="row" spacing={1}>
          <Tooltip title="Refresh">
            <IconButton onClick={fetchCompanies} size="small" sx={{ border: `1px solid ${border}`, borderRadius: 1.5, color: subtext }}>
              <RefreshIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          <Button variant="contained" startIcon={<AddIcon />} onClick={() => setDialogOpen(true)}
            sx={{ borderRadius: 2, textTransform: 'none', fontWeight: 800, fontSize: '0.8rem', background: '#a78bfa', color: '#000', '&:hover': { background: '#9061f9' } }}>
            New Company
          </Button>
        </Stack>
      </Stack>

      {/* ── Summary strip ── */}
      <Paper elevation={0} sx={{ p: 2, borderRadius: 3, border: `1px solid ${border}`, background: surface, mb: 2 }}>
        <Stack direction={isMobile ? 'column' : 'row'} spacing={1.5} alignItems={isMobile ? 'flex-start' : 'center'} justifyContent="space-between" flexWrap="wrap">
          <Stack direction="row" spacing={1} flexWrap="wrap">
            {[
              { label: `Active: ${rows.filter(r => r.isActive).length}`, color: '#34d399', bg: 'rgba(52,211,153,0.10)' },
              { label: `Pending: ${rows.filter(r => !r.isActive && !r.adminAccepted).length}`, color: '#fbbf24', bg: 'rgba(251,191,36,0.10)' },
              { label: `Suspended: ${rows.filter(r => !r.isActive && r.adminAccepted).length}`, color: '#f87171', bg: 'rgba(248,113,113,0.10)' },
              { label: `Users: ${rows.reduce((a, r) => a + (r.stats?.users ?? 0), 0)}`, color: '#a78bfa', bg: 'rgba(167,139,250,0.10)' },
              { label: `Devices: ${rows.reduce((a, r) => a + (r.stats?.devices ?? 0), 0)}`, color: '#fbbf24', bg: 'rgba(251,191,36,0.10)' },
              { label: `Vehicles: ${rows.reduce((a, r) => a + (r.stats?.vehicles ?? 0), 0)}`, color: '#f87171', bg: 'rgba(248,113,113,0.10)' },
            ].map(b => (
              <Box key={b.label} sx={{ px: 1.5, py: 0.5, borderRadius: 2, background: b.bg }}>
                <Typography sx={{ fontSize: '0.72rem', fontWeight: 800, color: b.color, letterSpacing: '0.04em' }}>{b.label}</Typography>
              </Box>
            ))}
          </Stack>
          <Typography sx={{ fontSize: '0.8rem', fontWeight: 700, color: subtext }}>
            This page: <strong style={{ color: headingColor }}>{rows.length}</strong>
          </Typography>
        </Stack>
      </Paper>

      {/* ── Filters ── */}
      <Paper elevation={0} sx={{ p: 2, borderRadius: 3, border: `1px solid ${border}`, background: surface, mb: 2 }}>
        <Stack direction={isMobile ? 'column' : 'row'} spacing={1.5} alignItems={isMobile ? 'stretch' : 'center'} justifyContent="space-between">
          <TextField size="small" value={search} onChange={e => setSearch(e.target.value)} placeholder="Search by name or email…"
            sx={{ minWidth: isMobile ? '100%' : 320, '& .MuiOutlinedInput-root': { borderRadius: 2, fontSize: '0.875rem', background: isDark ? 'rgba(255,255,255,0.04)' : 'rgba(0,0,0,0.03)', '& fieldset': { borderColor: border }, '&:hover fieldset': { borderColor: '#38bdf8' }, '&.Mui-focused fieldset': { borderColor: '#38bdf8' } } }}
            InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon fontSize="small" sx={{ color: subtext }} /></InputAdornment> }}
          />
          <Stack direction="row" spacing={0.5}>
            {STATUS_FILTERS.map(s => (
              <button key={s} onClick={() => setStatus(s)} style={{ padding: '4px 12px', borderRadius: 8, border: 'none', cursor: 'pointer', fontSize: '0.72rem', fontWeight: 800, letterSpacing: '0.06em', textTransform: 'uppercase', background: status === s ? '#38bdf8' : isDark ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.05)', color: status === s ? '#000' : subtext, transition: 'all 0.15s' }}>
                {s === 'all' ? 'All' : s.charAt(0).toUpperCase() + s.slice(1)}
              </button>
            ))}
          </Stack>
        </Stack>
        {error && <Alert severity="error" sx={{ mt: 1.5, borderRadius: 2, background: 'rgba(248,113,113,0.08)', border: '1px solid rgba(248,113,113,0.2)', color: '#f87171', '& .MuiAlert-icon': { color: '#f87171' } }}>{error}</Alert>}
      </Paper>

      {/* ── Table ── */}
      <Paper elevation={0} sx={{ borderRadius: 3, border: `1px solid ${border}`, background: surface, overflow: 'hidden' }}>
        <Box sx={{ px: 2, py: 1.25 }}>
          <Typography variant="body2" sx={{ color: subtext, fontSize: '0.78rem' }}>
            Page <strong style={{ color: headingColor }}>{page}</strong> of <strong style={{ color: headingColor }}>{pages}</strong>
            {' · '}Total: <strong style={{ color: headingColor }}>{total.toLocaleString()}</strong>
          </Typography>
        </Box>
        <Divider sx={{ borderColor: border }} />
        <Box sx={{ width: '100%', overflowX: 'auto' }}><Table>
          <TableHead>
            <TableRow sx={{ background: isDark ? 'rgba(255,255,255,0.03)' : 'rgba(0,0,0,0.03)' }}>
              {['Organization', 'Email', 'Users', 'Devices', 'Vehicles', 'Status', ''].map(h => (
                <TableCell key={h} sx={{ fontWeight: 800, fontSize: '0.7rem', letterSpacing: '0.08em', textTransform: 'uppercase', color: subtext, borderBottom: `1px solid ${border}`, py: 1.5 }}>{h}</TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {loading && rows.length === 0 ? (
              Array.from({ length: 6 }).map((_, i) => (
                <TableRow key={i}>{Array.from({ length: 7 }).map((_, j) => <TableCell key={j} sx={{ borderBottom: `1px solid ${border}` }}><Skeleton height={20} /></TableCell>)}</TableRow>
              ))
            ) : rows.length === 0 ? (
              <TableRow>
                <TableCell colSpan={7} sx={{ borderBottom: 'none' }}>
                  <Box sx={{ py: 8, textAlign: 'center' }}>
                    <BusinessIcon sx={{ fontSize: 36, color: subtext, mb: 1 }} />
                    <Typography sx={{ fontWeight: 800, color: headingColor }}>No companies yet</Typography>
                    <Typography variant="body2" sx={{ color: subtext, mt: 0.5, mb: 2 }}>Create your first company to get started</Typography>
                    <Button variant="contained" startIcon={<AddIcon />} onClick={() => setDialogOpen(true)} sx={{ borderRadius: 2, textTransform: 'none', fontWeight: 800, background: '#a78bfa', color: '#000', '&:hover': { background: '#9061f9' } }}>New Company</Button>
                  </Box>
                </TableCell>
              </TableRow>
            ) : rows.map(c => {
              const badge = getStatusBadge(c); // ← uses the helper function
              return (
                <TableRow key={c._id} hover sx={{ '&:hover': { background: isDark ? 'rgba(255,255,255,0.03)' : 'rgba(0,0,0,0.02)' }, '& td': { borderBottom: `1px solid ${border}` } }}>
                  <TableCell>
                    <Typography sx={{ fontWeight: 800, fontSize: '0.875rem', color: headingColor }}>{c.name || '—'}</Typography>
                    <Typography variant="caption" sx={{ color: subtext, fontFamily: 'monospace', fontSize: '0.68rem' }}>{c._id}</Typography>
                  </TableCell>
                  <TableCell>
                    <Typography sx={{ fontWeight: 700, fontSize: '0.8rem', color: isDark ? 'rgba(255,255,255,0.7)' : 'rgba(0,0,0,0.6)' }}>{c.email || '—'}</Typography>
                  </TableCell>
                  {[
                    { val: c.stats?.users, Icon: PeopleIcon, color: '#a78bfa' },
                    { val: c.stats?.devices, Icon: DevicesIcon, color: '#fbbf24' },
                    { val: c.stats?.vehicles, Icon: DirectionsCarIcon, color: '#f87171' },
                  ].map(({ val, Icon, color }, idx) => (
                    <TableCell key={idx}>
                      <Stack direction="row" spacing={0.75} alignItems="center">
                        <Icon sx={{ fontSize: 15, color }} />
                        <Typography sx={{ fontWeight: 800, fontSize: '0.875rem', color: headingColor }}>{val ?? 0}</Typography>
                      </Stack>
                    </TableCell>
                  ))}

                  {/* ── Status badge — uses getStatusBadge() ── */}
                  <TableCell>
                    <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5, px: 1.5, py: 0.4, borderRadius: 2, background: badge.bg }}>
                      <Box sx={{ width: 6, height: 6, borderRadius: '50%', background: badge.color }} />
                      <Typography sx={{ fontSize: '0.7rem', fontWeight: 800, letterSpacing: '0.06em', textTransform: 'uppercase', color: badge.color }}>
                        {badge.label}
                      </Typography>
                    </Box>
                  </TableCell>

                  <TableCell align="right">
                    <ActionsMenu
                      company={c}
                      onView={() => setOverviewId(c._id)}
                      onToggleStatus={() => setConfirmTarget(c)}
                      onDelete={() => setDeleteTarget(c)}
                    />
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table></Box>
        <Divider sx={{ borderColor: border }} />
        <Box sx={{ display: 'flex', justifyContent: 'flex-end', p: 2 }}>
          <Pagination count={pages} page={page} onChange={(_, p) => setPage(p)} size="small"
            sx={{ '& .MuiPaginationItem-root': { color: subtext, borderColor: border, '&.Mui-selected': { background: '#38bdf8', color: '#000', borderColor: '#38bdf8', fontWeight: 800 }, '&:hover': { background: 'rgba(56,189,248,0.08)' } } }}
          />
        </Box>
      </Paper>
    </Box>
  );
};

export default TenantCompaniesPage;